import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Table, Spinner, Modal, Badge, Card, Row, Col, Form } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import ExportButtons from "../../components/ExportButtons";

const DriverForApproval = () => {
  const [state, setState] = useState({});
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: "", label: "" });
  const [viewModal, setViewModal] = useState({ show: false, row: null });
  const [rowDates, setRowDates] = useState({}); // { rowId: { expiryDL, expiryInsurance, expiryPUC } }
  const dispatch = useDispatch();

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/DriverForApproval`;
  const API_URL_DL = `${API_WEB_URLS.MASTER}/0/token/UpdateDLExpiry`;
  const API_URL_PUC = `${API_WEB_URLS.MASTER}/0/token/UpdatePUCExpiry`;
  const API_URL_Insurance = `${API_WEB_URLS.MASTER}/0/token/UpdateInsuranceExpiry`;
  const API_URL_Approval = `${API_WEB_URLS.MASTER}/0/token/ApproveDriver`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
    } finally {
      setLoading(false);
    }
  }, [dispatch, API_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize rowDates from fetched data
  useEffect(() => {
    if (gridData && gridData.length > 0) {
      const initialDates = {};
      gridData.forEach(row => {
        if (row.DLExpiryDate || row.InsuranceExpiryDate || row.PUCExpiryDate) {
          initialDates[row.Id] = {
            expiryDL: row.DLExpiryDate || '',
            expiryInsurance: row.InsuranceExpiryDate || '',
            expiryPUC: row.PUCExpiryDate || ''
          };
        }
      });
      setRowDates(initialDates);
    }
  }, [gridData]);

  const data = useMemo(() => gridData || [], [gridData]);

  const buildImageUrl = (imageName) => {
    if (!imageName) return "";
    if (imageName.startsWith("http://") || imageName.startsWith("https://")) return imageName;
    return `${API_WEB_URLS.IMAGEURL}${imageName}`;
  };

  const handleApprovalClick = async (rowData, isReject = false) => {
    const val = isReject ? 2 : 1;
    setActionLoading(true);

    try {
      await Fn_FillListData(
        dispatch,
        setState,
        "nothing",
        `${API_URL_Approval}/${val}/${rowData.Id}`
      );
      setViewModal({ show: false, row: null });
      fetchData();
    } catch (err) {
      alert("Action failed: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDateToSMS = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleExpiryDLChange = (rowData, dateValue) => {
    const smsFormattedDate = formatDateToSMS(dateValue);
    setRowDates(prev => ({
      ...prev,
      [rowData.Id]: {
        ...prev[rowData.Id],
        expiryDL: dateValue
      }
    }));
    Fn_FillListData(dispatch, setState, "gridData", API_URL_DL + `/${smsFormattedDate}/${rowData.Id}`);
  };

  const handleExpiryInsuranceChange = (rowData, dateValue) => {
    const smsFormattedDate = formatDateToSMS(dateValue);
    setRowDates(prev => ({
      ...prev,
      [rowData.Id]: {
        ...prev[rowData.Id],
        expiryInsurance: dateValue
      }
    }));
    Fn_FillListData(dispatch, setState, "gridData", API_URL_Insurance + `/${smsFormattedDate}/${rowData.Id}`);
  };

  const handleExpiryPUCChange = (rowData, dateValue) => {
    const smsFormattedDate = formatDateToSMS(dateValue);
    setRowDates(prev => ({
      ...prev,
      [rowData.Id]: {
        ...prev[rowData.Id],
        expiryPUC: dateValue
      }
    }));
    Fn_FillListData(dispatch, setState, "nothing", API_URL_PUC + `/${smsFormattedDate}/${rowData.Id}`);
  };

  const documentKeys = useMemo(
    () => [
      { key: "UserPhoto", label: "Profile Photo" },
      { key: "AadharFront", label: "Aadhar Front" },
      { key: "AadharBack", label: "Aadhar Back" },
      { key: "RcFront", label: "RC Front" },
      { key: "RCBack", label: "RC Back" },
      { key: "DrivingLicenseFront", label: "DL Front" },
      { key: "DrivingLicenseBack", label: "DL Back" },
      { key: "Insurance", label: "Insurance Document" },
      { key: "PUC", label: "PUC Document" },
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: "#",
        id: "row",
        disableSortBy: true,
        Cell: ({ row }) => <span style={{ fontSize: 11 }}>{row.index + 1}</span>,
      },
      {
        Header: "Captain Name",
        accessor: "Name",
        Cell: ({ row, value }) => {
          const userPhoto = row.original.UserPhoto ? buildImageUrl(row.original.UserPhoto) : null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt="avatar"
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", border: "1px solid #E5E7EB" }}
                />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E87B2D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                  {(value || "C")[0].toUpperCase()}
                </div>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: "#1F2937" }}>{value || "-"}</span>
            </div>
          );
        },
      },
      { Header: "Mobile", accessor: "MobileNo", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "Email", accessor: "Email", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "PAN", accessor: "PanNo", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "Aadhar", accessor: "AadharNo", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "DL No", accessor: "DrivingLicence", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      {
        Header: "Bank Details",
        accessor: "BankName",
        disableSortBy: true,
        Cell: ({ row }) => {
          const r = row.original;
          if (!r.BankName && !r.AccountNo) return <span className="text-muted" style={{ fontSize: 11, fontStyle: "italic" }}>Not provided</span>;
          return (
            <div style={{ fontSize: 11, lineHeight: 1.3 }}>
              <strong>{r.BankName || "Bank"}</strong>
              <div style={{ color: "#2563EB" }}>A/C: {r.AccountNo || "-"}</div>
              <div style={{ color: "#6B7280", fontSize: 10 }}>IFSC: {r.IFSC || "-"}</div>
            </div>
          );
        },
      },
      {
        Header: "Action",
        id: "approval",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Button
                size="sm"
                variant="primary"
                className="py-0 px-2"
                style={{ fontSize: 11, backgroundColor: "#E87B2D", borderColor: "#E87B2D", fontWeight: 600 }}
                onClick={() => setViewModal({ show: true, row: row.original })}
              >
                👁️ View Details
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: "Name", desc: false }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div style={{ padding: "12px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 150 }}>
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  const activeDriver = viewModal.row;
  const activeDriverDates = activeDriver ? rowDates[activeDriver.Id] || {} : {};
  const isModalApproveDisabled = !activeDriverDates.expiryDL || !activeDriverDates.expiryInsurance || !activeDriverDates.expiryPUC;

  return (
    <div style={{ padding: "8px 12px", maxWidth: 1450, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #E87B2D" }}>
        <div>
          <h5 style={{ margin: 0, fontWeight: 700, color: "#1F2937", fontSize: 16 }}>🚖 Captain Approval Dossier</h5>
          <span style={{ fontSize: 11, color: "#6B7280" }}>Review captain registration details, bank accounts, documents, and manage approval status</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ExportButtons title="Driver For Approval Report" columns={columns} data={data} filename="Driver_For_Approval" />
          <input
            type="text"
            placeholder="Search Captain..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 160, height: 30, fontSize: 12, borderRadius: 6 }}
          />
        </div>
      </div>

      {/* Grid Table */}
      <div style={{ overflowX: "auto", marginBottom: 8, borderRadius: 8, border: "1px solid #E5E7EB" }}>
        <Table {...getTableProps()} size="sm" bordered hover style={{ fontSize: 12, minWidth: 1100, marginBottom: 0 }}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.disableSortBy ? {} : col.getSortByToggleProps())}
                    key={j}
                    style={{ padding: "6px 8px", fontSize: 11, fontWeight: 700, backgroundColor: "#E87B2D", color: "#fff", textTransform: "uppercase" }}
                  >
                    {col.render("Header")}
                    {!col.disableSortBy && col.isSorted && <span style={{ marginLeft: 3 }}>{col.isSortedDesc ? "▼" : "▲"}</span>}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted" style={{ fontSize: 12 }}>
                  No pending captain approvals found.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j} style={{ padding: "6px 8px", verticalAlign: "middle" }}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, flexWrap: "wrap", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Page {pageIndex + 1} of {pageOptions.length || 1}</span>
          <select className="form-select form-select-sm" style={{ width: 60, height: 26, fontSize: 11 }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => gotoPage(0)} disabled={!canPreviousPage}>|«</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => previousPage()} disabled={!canPreviousPage}>‹</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => nextPage()} disabled={!canNextPage}>›</button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: 11 }} onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>»|</button>
        </div>
      </div>

      {/* COMPREHENSIVE CAPTAIN DETAILS & APPROVAL MODAL */}
      <Modal
        show={viewModal.show}
        onHide={() => !actionLoading && setViewModal({ show: false, row: null })}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton style={{ backgroundColor: "#1F2937", color: "#fff", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <span>🚖 Captain Approval Dossier</span>
            {activeDriver && (
              <Badge bg="warning" text="dark" style={{ fontSize: 11 }}>
                Pending Approval #{activeDriver.Id}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        {activeDriver && (
          <Modal.Body style={{ padding: "16px", backgroundColor: "#F9FAFB" }}>
            {/* Captain Basic Profile Banner */}
            <Card className="mb-3 border-0 shadow-sm" style={{ backgroundColor: "#FFFFFF" }}>
              <Card.Body className="p-3">
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    {activeDriver.UserPhoto ? (
                      <img
                        src={buildImageUrl(activeDriver.UserPhoto)}
                        alt={activeDriver.Name}
                        style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "3px solid #E87B2D", cursor: "pointer" }}
                        onClick={() => setImageModal({ show: true, url: buildImageUrl(activeDriver.UserPhoto), label: "Profile Photo" })}
                      />
                    ) : (
                      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg, #E87B2D, #C25E14)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800 }}>
                        {(activeDriver.Name || "C")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h5 style={{ margin: 0, fontWeight: 800, color: "#111827", fontSize: 18 }}>{activeDriver.Name}</h5>
                    <div style={{ fontSize: 12, color: "#4B5563", marginTop: 2 }}>
                      <span>📱 <strong>{activeDriver.MobileNo || "-"}</strong></span>
                      <span style={{ margin: "0 8px" }}>•</span>
                      <span>✉️ <strong>{activeDriver.Email || "-"}</strong></span>
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                      📍 Address / Pincode: <strong>{activeDriver.Address || "-"} ({activeDriver.Pincode || "-"})</strong>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Row className="g-3 mb-3">
              {/* Personal & ID Information */}
              <Col md={6}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Header className="bg-white border-bottom fw-bold text-dark py-2" style={{ fontSize: 13 }}>
                    👤 Personal & Identity Proofs
                  </Card.Header>
                  <Card.Body className="py-2 px-3" style={{ fontSize: 12 }}>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">Father's Name:</span>
                      <span className="fw-semibold">{activeDriver.FatherName || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">PAN Number:</span>
                      <span className="fw-semibold font-monospace">{activeDriver.PanNo || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">Aadhar Number:</span>
                      <span className="fw-semibold font-monospace">{activeDriver.AadharNo || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Driving License No:</span>
                      <span className="fw-semibold font-monospace text-primary">{activeDriver.DrivingLicence || "-"}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Bank Account Details */}
              <Col md={6}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #2563EB" }}>
                  <Card.Header className="bg-white border-bottom fw-bold text-primary py-2" style={{ fontSize: 13 }}>
                    🏦 Captain Bank Account Details
                  </Card.Header>
                  <Card.Body className="py-2 px-3" style={{ fontSize: 12 }}>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">Bank Name:</span>
                      <span className="fw-bold text-dark">{activeDriver.BankName || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">Account Number:</span>
                      <span className="fw-bold text-primary font-monospace" style={{ fontSize: 13 }}>{activeDriver.AccountNo || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-muted">IFSC Code:</span>
                      <span className="fw-bold font-monospace text-dark">{activeDriver.IFSC || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">A/C Holder Name:</span>
                      <span className="fw-semibold">{activeDriver.AccountPersonName || activeDriver.Name || "-"}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Expiry Dates Setting Card */}
            <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: "4px solid #E87B2D" }}>
              <Card.Header className="bg-white border-bottom fw-bold py-2" style={{ fontSize: 13, color: "#E87B2D" }}>
                📅 Document Expiry Dates (Required Before Approval)
              </Card.Header>
              <Card.Body className="p-3">
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Expiry DL (Driving License) <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={activeDriverDates.expiryDL || ''}
                        onChange={(e) => handleExpiryDLChange(activeDriver, e.target.value)}
                        style={{ fontSize: 12 }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Expiry Insurance <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={activeDriverDates.expiryInsurance || ''}
                        onChange={(e) => handleExpiryInsuranceChange(activeDriver, e.target.value)}
                        style={{ fontSize: 12 }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Expiry PUC <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={activeDriverDates.expiryPUC || ''}
                        onChange={(e) => handleExpiryPUCChange(activeDriver, e.target.value)}
                        style={{ fontSize: 12 }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {isModalApproveDisabled && (
                  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "6px 10px", marginTop: 10, fontSize: 11, color: "#991B1B" }}>
                    ⚠️ Please fill all three expiry dates (DL, Insurance, PUC) above to enable the <strong>Approve</strong> button.
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Uploaded Documents Gallery */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom fw-bold text-dark py-2" style={{ fontSize: 13 }}>
                🖼️ Uploaded Captain Documents Gallery
              </Card.Header>
              <Card.Body className="p-3">
                <Row className="g-3">
                  {documentKeys.map((doc) => {
                    const val = activeDriver[doc.key] || (doc.key === "RcFront" ? activeDriver.RCFront : (doc.key === "RCBack" ? activeDriver.RcBack : null));
                    const imgUrl = val ? buildImageUrl(val) : "";

                    return (
                      <Col key={doc.key} xs={6} sm={4} md={3}>
                        <div
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 8,
                            padding: 8,
                            backgroundColor: "#FFFFFF",
                            textAlign: "center",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                            {doc.label}
                          </span>

                          {imgUrl ? (
                            <div
                              style={{ cursor: "pointer", position: "relative", marginBottom: 6 }}
                              onClick={() => setImageModal({ show: true, url: imgUrl, label: doc.label })}
                            >
                              <img
                                src={imgUrl}
                                alt={doc.label}
                                style={{
                                  width: "100%",
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  border: "1px solid #F3F4F6",
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              <Button
                                size="sm"
                                variant="dark"
                                className="py-0 px-1"
                                style={{ fontSize: 10, position: "absolute", bottom: 4, right: 4, opacity: 0.85 }}
                              >
                                🔍 View
                              </Button>
                            </div>
                          ) : (
                            <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "#F3F4F6", borderRadius: 6, color: "#9CA3AF", fontSize: 11, fontStyle: "italic" }}>
                              Not Uploaded
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
        )}

        <Modal.Footer style={{ backgroundColor: "#F3F4F6", justifyContent: "space-between" }}>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setViewModal({ show: false, row: null })}
              disabled={actionLoading}
            >
              Cancel
            </Button>
          </div>

          {activeDriver && (
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleApprovalClick(activeDriver, true)}
                disabled={actionLoading}
                style={{ fontWeight: 600 }}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : "✗ Reject Captain"}
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => handleApprovalClick(activeDriver, false)}
                disabled={isModalApproveDisabled || actionLoading}
                style={{ fontWeight: 700 }}
                title={isModalApproveDisabled ? "Please select Expiry DL, Expiry Insurance, and Expiry PUC dates" : ""}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : "✓ Confirm Approve Captain"}
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      {/* Fullscreen Image Preview Modal */}
      <Modal
        show={imageModal.show}
        onHide={() => setImageModal({ show: false, url: "", label: "" })}
        fullscreen
        className="driver-doc-modal"
      >
        <Modal.Header closeButton className="bg-dark border-secondary">
          <Modal.Title className="text-white">{imageModal.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="d-flex align-items-center justify-content-center p-0 bg-dark overflow-auto"
          style={{ minHeight: "calc(100vh - 60px)" }}
        >
          {imageModal.url && (
            <img
              src={imageModal.url}
              alt={imageModal.label}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 60px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.currentTarget.src = "";
                e.currentTarget.alt = "Image not found";
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DriverForApproval;
